// @flow
import {
	ArrowDownward,
	ArrowUpward,
	ClearOutlined,
	KeyboardArrowDownOutlined,
	KeyboardArrowRightOutlined,
	Print,
	Sort
} from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import { Collapse, FormControl, FormLabel, TextField, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid2";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import axios from "axios";
import _ from "lodash";
import { enqueueSnackbar } from "notistack";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import FormBoxTitle from "../../../components/mui/app/formBoxTitle/formBoxTitle";
import CustomAutocomplete from "../../../components/mui/form/autocomplete/autocomplete";
import DateRange from "../../../components/mui/form/dateRange/dateRange";
import { API } from "../../../helpers/constants";
import { handleAxiosErrors, logoutUnauthorizedUser } from "../../../helpers/errorHandling";
import {
	dashboard_filter_erp_code,
	dashboard_filter_customer,
} from "../../../helpers/rights/rights";
import { formControlDefaults, textFieldDefaults, textFieldSelectDefaults } from "../../../helpers/theme/defaultProps";
import { initialFilters, sortingOptions } from "../../../hooks/dashboard/useDashboard";
import useUserAction from "../../../hooks/user/useUserAction";
import useUserData from "../../../hooks/user/useUserData";
import { DASHBOARD } from "../../../locales/pages/namespaces";
import PermissionHOC from "../../../modules/permission/components/permissionHOC/permissionHOC";

type Props = {};

const Filters = (props: Props) => {
	const {
		loading,
		filters,
		onFilterChange,
		setFilters,
		orderByField,
		onSortChange,
		open,
		setOpen,
		sites,
		customers,
		isBelowLg,
		statuses,
		types,
		categories,
		assignees,
		internalAssignments,
		kinds
	} = props
	const { t } = useTranslation(DASHBOARD)
	const { dateFormat, token, isLoggedIn } = useUserData()
	const [sitesData, setSitesData] = useState([...sites])
	const [customersData, setCustomersData] = useState([...customers])
	const [categoriesData, setCategoriesData] = useState([...categories])
	const [assigneesData, setAssigneesData] = useState([...assignees])
	const [internalAssignmentsData, setInternalAssignmentsData] = useState([...(internalAssignments ?? [])])
	const [kindsData] = useState([...kinds])

	const [loadingSearch, setLoadingSearch] = useState(false)
	const { userLogout } = useUserAction()
	
	const onChangeCustomer = useCallback((newValue) => {
		setLoadingSearch(true)
		axios({
			method: 'post',
			data: {
				CustId: newValue?.id,
				ReturnData: ['addresses']
			},
			headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
			url: `${API}/en/issues/issueCustData`
		}).then((res) => {
			setLoadingSearch(false)
			setSitesData(_.orderBy(res.data?.addresses?.map(i => ({
				id: i.id,
				Description: i.Site
			})), ['Description'], ['asc']))
		}).catch((error) => {
			setLoadingSearch(false)
			enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
				variant: 'error',
				autoHideDuration: 3000,
			})
			logoutUnauthorizedUser(error, isLoggedIn, userLogout)
		})
	}, [t, isLoggedIn, userLogout])
	
	const searchCustomers = useCallback((evt) => {
		setLoadingSearch(true)
		axios({
			method: 'post',
			headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
			data: {
				query: evt.target.value,
			},
			url: `${API}/en/customers/search`
		}).then((response) => {
			setLoadingSearch(false)
			setCustomersData(response.data.data)
		}).catch((error) => {
			enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
				variant: 'error',
				autoHideDuration: 3000,
			})
			setLoadingSearch(false)
			logoutUnauthorizedUser(error, isLoggedIn, userLogout)
		})
	}, [t, isLoggedIn, userLogout])
	
	const searchSites = useCallback((evt) => {
		setLoadingSearch(true)
		axios({
			method: 'post',
			headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
			data: {
				query: evt.target.value,
				...(filters.customer && filters.customer?.id && { CustId: filters.customer?.id }),
				Type: 'Site'
			},
			url: `${API}/en/customer/address/search`
		}).then((response) => {
			setSitesData(response.data.data)
			setLoadingSearch(false)
		}).catch((error) => {
			setLoadingSearch(false)
			enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
				variant: 'error',
				autoHideDuration: 3000,
			})
			logoutUnauthorizedUser(error, isLoggedIn, userLogout)
		})
	}, [filters.customer, t, isLoggedIn, userLogout])

	const searchAssignees = useCallback((evt) => {
		const customerIds = filters.customer?.id ? String(filters.customer.id) : ''
		setLoadingSearch(true)
		axios({
			method: 'post',
			headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
			url: `${API}/en/custom/action`,
			data: {
				action_id: 'assignee-search',
				fields: {
					CustId: customerIds,
					query: evt.target.value
				},
			},
		}).then((response) => {
			setAssigneesData(response.data.data)
			setLoadingSearch(false)
		}).catch((error) => {
			setLoadingSearch(false)
			enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
				variant: 'error',
				autoHideDuration: 3000,
			})
			logoutUnauthorizedUser(error, isLoggedIn, userLogout)
		})
	}, [filters.customer, t, isLoggedIn, userLogout])
	
	const searchInternalAssignments = useCallback((evt) => {
		setLoadingSearch(true)
		axios({
			method: 'post',
			headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
			url: `${API}/en/issue/assignee/search`,
			data: {
				query: evt.target.value,
				Internal: true
			},
		}).then((response) => {
			setInternalAssignmentsData(response.data.data)
			setLoadingSearch(false)
		}).catch((error) => {
			setLoadingSearch(false)
			enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
				variant: 'error',
				autoHideDuration: 3000,
			})
			logoutUnauthorizedUser(error, isLoggedIn, userLogout)
		})
	}, [t, isLoggedIn, userLogout])
	
	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', marginBottom: 1, marginTop: 1 }}>
			<FormBoxTitle
				title={t('Filter Options')}
			>
				<Typography
					variant={'button'}
					component={'div'}
					onClick={() => setOpen(!open)}
					sx={{
						cursor: 'pointer',
						padding: 0.5,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center'
					}}
				>
					{open ? <KeyboardArrowDownOutlined sx={{ fontSize: 16 }}/> :
						<KeyboardArrowRightOutlined sx={{ fontSize: 16 }}/>}
				</Typography>
			</FormBoxTitle>
			<Collapse in={open}>
				<Grid container rowSpacing={0.5} columnSpacing={1} alignItems={'end'}>
					<PermissionHOC requiredPermissions={[dashboard_filter_customer]}>
						<Grid size={isBelowLg ? 12 : 3}>
							<FormControl
								{...formControlDefaults}
							>
								<FormLabel>{t('Customer')}</FormLabel>
								<CustomAutocomplete
									identifier={'id'}
									value={filters?.customer}
									onChange={(evt, newValue, reason) => {
										setFilters({
											...filters,
											customer: newValue
										})
										if (reason === 'clear') {
											setCustomersData([...customers])
											setSitesData([...sites])
										} else {
											onChangeCustomer(newValue)
										}
									}}
									options={customersData}
									labelKey={'Description'}
									onInputChange={(evt) => searchCustomers(evt)}
									otherprops={{
										selectOnFocus: true,
										handleHomeEndKeys: true,
										getOptionKey: (option) => (option?.id)
									}}
									loading={loadingSearch}
								/>
							</FormControl>
						</Grid>
					</PermissionHOC>
					<Grid size={isBelowLg ? 12 : 3}>
						<FormControl
							{...formControlDefaults}
						>
							<FormLabel>{t('Site')}</FormLabel>
							<CustomAutocomplete
								identifier={'id'}
								value={filters?.sites}
								onChange={(evt, newValue, reason) => {
									setFilters({
										...filters,
										sites: newValue
									})
									if (reason === 'clear' && !filters.customer?.id) {
										setSitesData([...sites])
									}
								}}
								options={sitesData}
								labelKey={'Description'}
								onInputChange={(evt) => searchSites(evt)}
								otherprops={{
									multiple: true,
									disableCloseOnSelect: true,
									limitTags: 1,
									getOptionKey: (option) => (option?.id)
								}}
								loading={loadingSearch}
							/>
						</FormControl>
					</Grid>
					<Grid size={isBelowLg ? 12 : 3}>
						<FormControl
							{...formControlDefaults}
						>
							<FormLabel>{t('Status')}</FormLabel>
							<CustomAutocomplete
								identifier="id"
								name="status"
								onChange={(evt, newValue, reason) => {
									setFilters({
										...filters,
										status: newValue
									})
								}}
								value={filters.status}
								options={statuses}
								labelKey={'Description'}
								otherprops={{
									multiple: true,
									disableCloseOnSelect: true,
									limitTags: 1,
									getOptionKey: (option) => (option?.id)
								}}
							/>
						</FormControl>
					</Grid>
					<Grid size={isBelowLg ? 12 : 3}>
						<FormControl
							{...formControlDefaults}
						>
							<FormLabel>{t('Dimension')}</FormLabel>
							<CustomAutocomplete
								identifier="id"
								name="type"
								onChange={(evt, newValue, reason) => {
									setFilters({
										...filters,
										type: newValue
									})
								}}
								value={filters.type}
								options={types}
								labelKey={'Description'}
								otherprops={{
									multiple: true,
									disableCloseOnSelect: true,
									limitTags: 1,
									getOptionKey: (option) => (option?.id)
								}}
							/>
						</FormControl>
					</Grid>
					<Grid size={isBelowLg ? 12 : 3}>
						<FormControl
							{...formControlDefaults}
						>
							<FormLabel>{t('Service Category')}</FormLabel>
							<CustomAutocomplete
								identifier="id"
								name="categories"
								onChange={(evt, newValue, reason) => {
									setFilters({
										...filters,
										categories: newValue
									})
								}}
								value={filters.categories}
								options={categoriesData}
								labelKey={'Description'}
								otherprops={{
									multiple: true,
									disableCloseOnSelect: true,
									limitTags: 1,
									getOptionKey: (option) => (option?.id)
								}}
								loading={loadingSearch}
							/>
						</FormControl>
					</Grid>
					<Grid size={isBelowLg ? 12 : 3}>
						<FormControl
							{...formControlDefaults}
						>
					<FormLabel>{t('Subcontractor')}</FormLabel>
						<CustomAutocomplete
							identifier="id"
							name="assignees"
							onChange={(evt, newValue, reason) => {
								setFilters({
									...filters,
									assignees: newValue
								})
							}}
							value={filters.assignees}
							options={assigneesData}
							labelKey={'Description'}
							onInputChange={(evt) => searchAssignees(evt)}
							otherprops={{
								multiple: true,
								disableCloseOnSelect: true,
								limitTags: 1,
								getOptionKey: (option) => (option?.id)
							}}
							loading={loadingSearch}
						/>
					</FormControl>
				</Grid>
				<Grid size={isBelowLg ? 12 : 3}>
					<FormControl
						{...formControlDefaults}
					>
						<FormLabel>{t('Internal Assignment')}</FormLabel>
						<CustomAutocomplete
							identifier="id"
							name="internalAssignments"
							onChange={(evt, newValue, reason) => {
								setFilters({
									...filters,
									internalAssignments: newValue
								})
							}}
							value={filters.internalAssignments}
							options={internalAssignmentsData}
							labelKey={'Description'}
							onInputChange={(evt) => searchInternalAssignments(evt)}
							otherprops={{
								multiple: true,
								disableCloseOnSelect: true,
								limitTags: 1,
								getOptionKey: (option) => (option?.id)
							}}
							loading={loadingSearch}
						/>
					</FormControl>
				</Grid>
					<Grid size={isBelowLg ? 12 : 3}>
						<FormControl
							{...formControlDefaults}
						>
							<FormLabel>{t('Work Type')}</FormLabel>
							<CustomAutocomplete
								identifier="id"
								name="kind"
								onChange={(evt, newValue, reason) => {
									setFilters({
										...filters,
										kind: newValue
									})
								}}
								value={filters.kind}
								options={kindsData}
								labelKey={'Description'}
								otherprops={{
									multiple: true,
									disableCloseOnSelect: true,
									limitTags: 1,
									getOptionKey: (option) => (option?.id)
								}}
							/>
						</FormControl>
					</Grid>
					<Grid size={isBelowLg ? 12 : 3}>
						<FormControl
							{...formControlDefaults}
						>
							<FormLabel>{t('Targeted Start')}</FormLabel>
							<DateRange
								dateFormat={dateFormat}
								value={filters.targetedStartDt}
								onChange={(newValue) => {
									setFilters(prev => ({
										...prev,
										targetedStartDt: newValue
									}))
								}}
								onClear={() => (
									setFilters(prev => ({
										...prev,
										targetedStartDt: null
									}))
								)}
							/>
						</FormControl>
					</Grid>
					<Grid size={isBelowLg ? 12 : 3}>
						<FormControl
							{...formControlDefaults}
						>
							<FormLabel>{t('Targeted Expiry')}</FormLabel>
							<DateRange
								dateFormat={dateFormat}
								value={filters.targetedEndDt}
								onChange={(newValue) => {
									setFilters(prev => ({
										...prev,
										targetedEndDt: newValue
									}))
								}}
								onClear={() => (
									setFilters(prev => ({
										...prev,
										targetedEndDt: null
									}))
								)}
							/>
						</FormControl>
					</Grid>
					<Grid size={isBelowLg ? 12 : 3}>
						<FormControl
							{...formControlDefaults}
						>
							<FormLabel>{t('Actual End')}</FormLabel>
							<DateRange
								dateFormat={dateFormat}
								value={filters.actualEndDt}
								onChange={(newValue) => {
									setFilters(prev => ({
										...prev,
										actualEndDt: newValue
									}))
								}}
								onClear={() => (
									setFilters(prev => ({
										...prev,
										actualEndDt: null
									}))
								)}
							/>
						</FormControl>
					</Grid>
					<Grid size={isBelowLg ? 12 : 3}>
						<FormControl
							{...formControlDefaults}
						>
							<FormLabel>{t('Submit Date')}</FormLabel>
							<DateRange
								dateFormat={dateFormat}
								value={filters.submitDt}
								onChange={(newValue) => {
									setFilters(prev => ({
										...prev,
										submitDt: newValue
									}))
								}}
								onClear={() => (
									setFilters(prev => ({
										...prev,
										submitDt: null
									}))
								)}
							/>
						</FormControl>
					</Grid>
					<Grid size={isBelowLg ? 12 : 3}>
						<FormControl
							{...formControlDefaults}
						>
							<FormLabel>{t('Reference Code')}</FormLabel>
							<TextField
								{...textFieldDefaults}
								value={filters?.erpCode2}
								onChange={(evt) => {
									setFilters({
										...filters,
										erpCode2: evt.target.value
									})
								}}
								name={'erpCode2'}
								slotProps={{
									htmlInput: {
										maxLength: 50
									}
								}}
							/>
						</FormControl>
					</Grid>
					<Grid size={isBelowLg ? 12 : 3}>
						<FormControl
							{...formControlDefaults}
						>
							<FormLabel>{t('Code')}</FormLabel>
							<TextField
								{...textFieldDefaults}
								value={filters?.code}
								onChange={(evt) => {
									setFilters({
										...filters,
										code: evt.target.value
									})
								}}
								name={'code'}
								slotProps={{
									htmlInput: {
										maxLength: 50
									}
								}}
							/>
						</FormControl>
					</Grid>
					<PermissionHOC requiredPermissions={[dashboard_filter_erp_code]}>
						<Grid size={isBelowLg ? 12 : 3}>
							<FormControl
								{...formControlDefaults}
							>
								<FormLabel>{t('Erp Code')}</FormLabel>
								<TextField
									{...textFieldDefaults}
									value={filters?.erpCode}
									onChange={(evt) => {
										setFilters({
											...filters,
											erpCode: evt.target.value
										})
									}}
									name={'erpCode'}
									slotProps={{
										htmlInput: {
											maxLength: 50
										}
									}}
								/>
							</FormControl>
						</Grid>
					</PermissionHOC>
					<Grid size={12} sx={{background: '#f6f6f6', p: 0.4}}>
						<Box>
							<Grid container columnSpacing={1} rowSpacing={0.5} justifyContent={'center'} alignItems={'end'}>
								<Grid size={isBelowLg ? 12 : 3}>
									<FormControl
										{...formControlDefaults}
									>
										<FormLabel>{t('Sort')}</FormLabel>
										<Box sx={{
											display: 'flex',
											flexDirection: 'row',
											alignItems: 'center',
											justifyContent: 'space-between',
											gap: 1
										}}>
											<TextField
												{...textFieldSelectDefaults}
												onChange={(evt) => {
													onSortChange(evt.target.value, orderByField?.direction)
												}}
												value={orderByField?.field}
												name={'Order By'}
											>
												{sortingOptions.map((type) => (
													<MenuItem
														key={type.name}
														value={type.name}
													>
														{t(type.label)}
													</MenuItem>
												))}
											</TextField>
											<IconButton
												color="secondary"
												loading={loading}
												sx={{ border: '1px solid black', borderRadius: 1, height: 26, width: 26 }}
												onClick={() => {
													onSortChange(orderByField?.field, orderByField?.direction === 'ascending' ? 'descending' : 'ascending')
												}}
											>
												{orderByField?.direction === 'ascending' ? (
													<ArrowUpward sx={{ fontSize: 18 }}/>) : (
													<ArrowDownward sx={{ fontSize: 18 }}/>)}
											</IconButton>
										</Box>
									</FormControl>
								</Grid>
								<Grid size={isBelowLg ? 6 : 2} sx={{ textAlign: 'center' }}>
									<Button
										loading={loading}
										startIcon={<SearchIcon/>}
										variant="outlined"
										color={'success'}
										onClick={() => {
								onFilterChange({
											customer: filters.customer,
											sites: filters.sites,
											targetedStartDt: filters.targetedStartDt,
											targetedEndDt: filters.targetedEndDt,
											actualEndDt: filters.actualEndDt,
											submitDt: filters.submitDt,
											erpCode2: filters.erpCode2,
											erpCode: filters.erpCode,
											code: filters.code,
											type: filters.type,
											categories: filters.categories,
											assignees: filters.assignees,
											internalAssignments: filters.internalAssignments,
											kind: filters.kind,
											status: filters.status
										})
										}}
									>
										{t('Search')}
									</Button>
								</Grid>
								<Grid size={isBelowLg ? 6 : 2} sx={{ textAlign: 'center' }}>
									<Button
										loading={loading}
										startIcon={<ClearOutlined/>}
										type={'submit'}
										variant="outlined"
										color={'error'}
										onClick={() => {
									onFilterChange(initialFilters)
										setFilters(initialFilters)
										setCustomersData([...customers])
										setSitesData([...sites])
										setCategoriesData([...categories])
										setAssigneesData([...assignees])
										setInternalAssignmentsData([...(internalAssignments ?? [])])
										}}
									>
										{t('Clear')}
									</Button>
								</Grid>
							</Grid>
						</Box>
					</Grid>
					{open && !isBelowLg && (
						<Grid size={12}>
							<Divider/>
						</Grid>
					)}
				</Grid>
			</Collapse>
		</Box>
	);
};

export default Filters



